/**
 * Accounting API (client-safe)
 *
 * No .server suffix so client hooks can import without triggering
 * TanStack Start import-protection. Handlers run only on the server.
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

/** Validates the three-part structure of a JWT (header.payload.signature). */
const jwtSchema = z
  .string()
  .min(1)
  .regex(/^[\w-]+\.[\w-]+\.[\w-]+$/, "Invalid JWT format");

/** Create a Supabase admin client using the Service Role Key (server-only). */
async function createAdminClient() {
  const { createClient } = await import("@supabase/supabase-js");

  const url = process.env.SUPABASE_URL ?? process.env.VITE_SUPABASE_URL;
  const serviceKey = process.env.SUPABASE_SERVICE_ROLE_KEY;

  if (!url || !serviceKey) {
    return {
      client: null as null,
      error:
        "Missing SUPABASE_URL or SUPABASE_SERVICE_ROLE_KEY on server. Set these as environment variables.",
    };
  }

  const client = createClient(url, serviceKey, {
    auth: { persistSession: false, autoRefreshToken: false },
  });

  return { client, error: null as null };
}

/**
 * Fetch financial transactions for the authenticated user.
 * Optionally filter by date range, type, and account.
 */
export const getTransactions = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      userId: z.string().uuid(),
      accessToken: jwtSchema,
      from: z.string().optional(),
      to: z.string().optional(),
      type: z
        .enum(["income", "expense", "transfer", "adjustment"])
        .optional(),
      accountId: z.string().uuid().optional(),
    })
  )
  .handler(async ({ data }) => {
    const { client, error: initError } = await createAdminClient();
    if (!client) return { transactions: [], error: initError };

    const {
      data: { user },
      error: authError,
    } = await client.auth.getUser(data.accessToken);
    if (authError || !user) return { transactions: [], error: "Unauthorized" };

    let query = client
      .from("transactions")
      .select(
        "id, user_id, type, account_id, category_id, amount, currency, date, description, is_private, tags, created_at"
      )
      .eq("user_id", data.userId);

    if (data.from) {
      query = query.gte("date", data.from);
    }
    if (data.to) {
      query = query.lte("date", data.to);
    }
    if (data.type) {
      query = query.eq("type", data.type);
    }
    if (data.accountId) {
      query = query.eq("account_id", data.accountId);
    }

    const { data: transactions, error } = await query.order("date", {
      ascending: false,
    });

    if (error) return { transactions: [], error: error.message };
    return { transactions: transactions ?? [], error: null };
  });

/**
 * Fetch the chart of accounts for the authenticated user.
 */
export const getAccounts = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      userId: z.string().uuid(),
      accessToken: jwtSchema,
    })
  )
  .handler(async ({ data }) => {
    const { client, error: initError } = await createAdminClient();
    if (!client) return { accounts: [], error: initError };

    const {
      data: { user },
      error: authError,
    } = await client.auth.getUser(data.accessToken);
    if (authError || !user) return { accounts: [], error: "Unauthorized" };

    const { data: accounts, error } = await client
      .from("accounts")
      .select("id, user_id, name, type, kind, is_default, created_at")
      .eq("user_id", data.userId)
      .order("name", { ascending: true });

    if (error) return { accounts: [], error: error.message };
    return { accounts: accounts ?? [], error: null };
  });

/**
 * Create a financial transaction for the authenticated user.
 * Whitelisted fields prevent privilege escalation.
 */
export const createTransaction = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      userId: z.string().uuid(),
      accessToken: jwtSchema,
      type: z.enum(["income", "expense", "transfer", "adjustment"]),
      accountId: z.string().uuid(),
      amount: z.number().positive(),
      currency: z.string().length(3).default("AUD"),
      date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD"),
      description: z.string().max(1000).optional().nullable(),
      categoryId: z.string().uuid().optional().nullable(),
    })
  )
  .handler(async ({ data }) => {
    const { client, error: initError } = await createAdminClient();
    if (!client) return { transaction: null, error: initError };

    const {
      data: { user },
      error: authError,
    } = await client.auth.getUser(data.accessToken);
    if (authError || !user) return { transaction: null, error: "Unauthorized" };

    const { data: transaction, error } = await client
      .from("transactions")
      .insert({
        user_id: data.userId,
        type: data.type,
        account_id: data.accountId,
        amount: data.amount,
        currency: data.currency,
        date: data.date,
        description: data.description ?? null,
        category_id: data.categoryId ?? null,
      })
      .select(
        "id, user_id, type, account_id, category_id, amount, currency, date, description, is_private, tags, created_at"
      )
      .single();

    if (error) return { transaction: null, error: error.message };

    // Publish Mycelium event for transaction creation
    const { publishMyceliumEvent } = await import("./_mycelium.server");
    await publishMyceliumEvent(client, {
      domain: "accounting",
      type: "transaction_created",
      userId: data.userId,
      payload: {
        transactionId: (transaction as Record<string, unknown>).id,
        type: data.type,
        amount: data.amount,
        currency: data.currency,
        accountId: data.accountId,
      },
    });

    return { transaction, error: null };
  });
